<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { Plus } from "@lucide/vue";

import { type RecentProject } from "@/composables/useRecentProjects";
import { type SwitcherProject } from "@/composables/useProjectSwitcherProjects";
import LoadingSpinner from "@/components/Reusables/Loading/LoadingSpinner.vue";
import { useStartProjectModal } from "@/pages/Projects/composables/useStartProjectModal.composable";

const props = defineProps<{
  recents: RecentProject[];
  allProjects: SwitcherProject[];
  isLoading: boolean;
}>();

const emit = defineEmits<{ navigate: [] }>();

const route = useRoute();
const currentProjectId = computed(() => (typeof route.params.id === "string" ? route.params.id : undefined));

const searchQuery = ref("");
const searchInput = ref<HTMLInputElement | null>(null);

const trimmedQuery = computed(() => searchQuery.value.trim().toLowerCase());

// Word-start matching, mirroring OrganizationsDropdown for consistency.
const matchesQuery = (name: string, query: string): boolean => {
  const lowered = name.toLowerCase();
  if (lowered.startsWith(query)) return true;
  return lowered.split(/[\s\-_./,&]+/).some((token) => token.startsWith(query));
};

const recentsExcludingCurrent = computed<SwitcherProject[]>(() =>
  props.recents
    .filter((recent) => recent.id !== currentProjectId.value)
    .map((recent) => ({ id: recent.id, name: recent.name, isActive: recent.isActive }))
);

const recentIds = computed(() => new Set(recentsExcludingCurrent.value.map((recent) => recent.id)));

// Browse list (idle): everything except your current project and what's already in Recent.
const otherProjects = computed<SwitcherProject[]>(() =>
  props.allProjects.filter((project) => project.id !== currentProjectId.value && !recentIds.value.has(project.id))
);

// Live filter across the full list while typing.
const filteredProjects = computed<SwitcherProject[]>(() => {
  if (!trimmedQuery.value) return [];
  return props.allProjects.filter(
    (project) => project.id !== currentProjectId.value && matchesQuery(project.name, trimmedQuery.value)
  );
});

// Spinner only when there is truly nothing to show yet — keep subsequent opens
// instant by rendering cached data without a flicker.
const showInitialSpinner = computed(() => props.isLoading && props.allProjects.length === 0);

onMounted(() => searchInput.value?.focus());
const { openStartProjectModal } = useStartProjectModal();

const handleCreateProject = async () => {
  emit("navigate");
  await openStartProjectModal();
};
</script>

<template>
  <div class="flex w-72 flex-col">
    <div class="border-b border-slate-200">
      <input
        ref="searchInput"
        v-model="searchQuery"
        type="text"
        placeholder="Find a project..."
        aria-label="Find a project"
        class="t-small placeholder:text-type-muted/70 w-full bg-transparent px-4 py-2 outline-none" />
    </div>

    <div class="max-h-72 overflow-y-auto p-1">
      <div
        v-if="showInitialSpinner"
        class="flex items-center justify-center py-4">
        <LoadingSpinner
          size="sm"
          fill="gray" />
      </div>

      <template v-else-if="trimmedQuery">
        <RouterLink
          v-for="project in filteredProjects"
          :key="project.id"
          :to="`/project/${project.id}`"
          role="menuitem"
          class="focus-ring interactive-active gap-px-8 text-type-default duration-fast ease-standard flex w-full items-center rounded-md px-3 py-2 text-left transition-colors"
          @click="emit('navigate')">
          <span
            role="img"
            :aria-label="project.isActive ? 'Active' : 'Not active'"
            :class="['h-2 w-2 shrink-0 rounded-full', project.isActive ? 'bg-app-green-500' : 'bg-app-yellow-500']" />
          <span class="t-xs flex-1 truncate text-inherit">{{ project.name }}</span>
        </RouterLink>

        <p
          v-if="filteredProjects.length === 0"
          class="t-xs text-type-muted px-3 py-3 text-center">
          No projects found
        </p>
      </template>

      <template v-else>
        <template v-if="recentsExcludingCurrent.length > 0">
          <p class="t-caption-bold text-slate-450 pb-px-6 px-3 font-medium tracking-wide uppercase">Recent projects</p>
          <RouterLink
            v-for="project in recentsExcludingCurrent"
            :key="`recent-${project.id}`"
            :to="`/project/${project.id}`"
            role="menuitem"
            class="focus-ring interactive-active gap-px-8 text-type-default duration-fast ease-standard flex w-full items-center rounded-md px-3 py-2 text-left transition-colors"
            @click="emit('navigate')">
            <span
              role="img"
              :aria-label="project.isActive ? 'Active' : 'Not active'"
              :class="['h-2 w-2 shrink-0 rounded-full', project.isActive ? 'bg-app-green-500' : 'bg-app-yellow-500']" />
            <span class="t-xs flex-1 truncate text-inherit">{{ project.name }}</span>
          </RouterLink>
        </template>

        <template v-if="otherProjects.length > 0">
          <p class="t-caption-bold text-slate-450 pb-px-6 pt-px-8 px-3 font-medium tracking-wide uppercase">All projects</p>
          <RouterLink
            v-for="project in otherProjects"
            :key="`all-${project.id}`"
            :to="`/project/${project.id}`"
            role="menuitem"
            class="focus-ring interactive-active gap-px-8 text-type-default duration-fast ease-standard flex w-full items-center rounded-md px-3 py-2 text-left transition-colors"
            @click="emit('navigate')">
            <span
              role="img"
              :aria-label="project.isActive ? 'Active' : 'Not active'"
              :class="['h-2 w-2 shrink-0 rounded-full', project.isActive ? 'bg-app-green-500' : 'bg-app-yellow-500']" />
            <span class="t-xs flex-1 truncate text-inherit">{{ project.name }}</span>
          </RouterLink>
        </template>

        <p
          v-if="recentsExcludingCurrent.length === 0 && otherProjects.length === 0"
          class="t-xs text-type-muted px-3 py-3 text-center">
          No projects available
        </p>
      </template>
    </div>

    <div class="border-t border-slate-200 p-1">
      <!-- A button, not a link: creating a project is a dialog now, and there is no route to point at
           since the create wizard was removed. The dropdown still closes on the way. -->
      <button
        type="button"
        role="menuitem"
        data-cy="switcher-create-project"
        class="focus-ring interactive-active gap-px-8 text-type-default duration-fast ease-standard hover:bg-app-blue-200/30 flex w-full items-center rounded-md px-3 py-2 text-left transition-colors"
        @click="handleCreateProject">
        <Plus
          :stroke-width="1.5"
          class="icon-sm shrink-0"
          aria-hidden="true" />
        <span class="t-small flex-1 text-inherit">New project</span>
      </button>
    </div>
  </div>
</template>
